import { Box, Grid, Paper } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'
import { APIError } from 'components/core/errors/api'
import { Rectangle } from 'components/core/rectangle'
import { ListReturn, Status } from 'lib/rpc'
import React, { useState } from 'react'
import { useStyles } from './styles'

type BlockListProps<T> = {
    items: ListReturn<T> | T[]
    renderItem: (T) => React.ReactNode
    emptyText: React.ReactNode
}

function BlockList<T>({ items, renderItem, emptyText }: BlockListProps<T>): React.ReactElement {
    const theme = useTheme()
    const classes = useStyles(theme)
    const [itemsArray, setItemsArray] = useState<Array<T> | null>(null)

    const getTableContents = (tableItems): React.ReactElement => {
        if (tableItems && tableItems.length > 0) {
            return (
                <Box>
                    {tableItems.map((item, i) => (
                        <Box mb={1} key={i}>
                            {/* setting padding to 0 here for information density.
                            The content can decide how much spacing is needed in this case */}
                            <Rectangle padding={2}>{renderItem(item)}</Rectangle>
                        </Box>
                    ))}
                </Box>
            )
        }
        return (
            <Box flexGrow={1}>
                <Rectangle padding={2}>{emptyText}</Rectangle>
            </Box>
        )
    }

    const contents = (): React.ReactElement => {
        // can pass a simple array of items or a ListReturn of items
        if (Array.isArray(items)) {
            if (items !== itemsArray) {
                setItemsArray(items)
            }
            return getTableContents(itemsArray)
        }
        switch (items.status) {
            case Status.ERROR:
                return (
                    <Box>
                        <Rectangle padding={1}>
                            <APIError err={items.error} />
                        </Rectangle>
                    </Box>
                )
            case Status.INIT:
            case Status.LOADING:
                if (itemsArray === null || itemsArray.length === 0) {
                    return (
                        <Box>
                            <Rectangle padding={1}>
                                <Skeleton />
                                <Skeleton animation={false} />
                                <Skeleton animation="wave" />
                            </Rectangle>
                        </Box>
                    )
                }
            // falls through intentionally to keep already loaded itemsArray
            case Status.OK:
                if (items.status === Status.OK && items.items !== itemsArray) {
                    setItemsArray(items.items)
                }
                return getTableContents(itemsArray)
            // no default
        }
    }

    return (
        <Grid container direction="row">
            <Grid item xs={12}>
                <Paper elevation={0} className={classes.paperContainer}>
                    {contents()}
                </Paper>
            </Grid>
        </Grid>
    )
}

export default BlockList
